import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import Loader from "../components/Loader";
import Breadcrumbs from "../components/Breadcrumbs";
import api from "../api";
import "../assets/buttons.css";
import "../assets/panels.css";

const BlockShow = () => {
  const { id } = useParams();
  const { hasPermission } = useAuth();

  const [block, setBlock] = useState(null);
  const [properties, setProperties] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Property search for adding to block
  const [propertySearch, setPropertySearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Properties table state
  const [propertiesPerPage, setPropertiesPerPage] = useState(30);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [propertiesSearchFilter, setPropertiesSearchFilter] = useState("");

  // Certificate filters and pagination
  const [certificatesPage, setCertificatesPage] = useState(1);
  const [certificatesPerPage, setCertificatesPerPage] = useState(10);
  const [certificatesSearch, setCertificatesSearch] = useState('');
  const [certificateTypeFilter, setCertificateTypeFilter] = useState('All');
  const [certificateStatusFilter, setCertificateStatusFilter] = useState('All');
  const [certificatePropertyTypeFilter, setCertificatePropertyTypeFilter] = useState('Active only');

  useEffect(() => {
    loadBlockData();
  }, [id]);

  const loadBlockData = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await api.get(`/blocks/${id}`);
      const data = response.data;

      if (data.success) {
        setBlock(data.block);
        setProperties(data.properties || []);
        setCertificates(data.certificates || []);
        setReports(data.reports || []);
      } else {
        setError(data.message || "Failed to load block");
      }
    } catch (err) {
      console.error("Error loading block:", err);
      setError(err.response?.data?.message || "Failed to load block");
    } finally {
      setLoading(false);
    }
  };

  // Search for properties to add to block
  const handlePropertySearch = async (searchTerm) => {
    setPropertySearch(searchTerm);

    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await api.get(`/properties/client/${block.client_id}/search/${encodeURIComponent(searchTerm)}`);
      if (response.data && Array.isArray(response.data)) {
        const availableProperties = response.data.filter(
          p => !properties.some(bp => bp.id === p.id)
        );
        setSearchResults(availableProperties.slice(0, 10));
      }
    } catch (err) {
      console.error("Error searching properties:", err);
    } finally {
      setSearching(false);
    }
  };

  // Add property to block
  const handleAddProperty = async (propertyId) => {
    try {
      const response = await api.get(`/blocks/${id}/property/${propertyId}/add`);
      if (response.data.type === "success") {
        toast.success(response.data.message);
        setPropertySearch("");
        setSearchResults([]);
        loadBlockData();
      } else {
        toast.error(response.data.message);
      }
    } catch (err) {
      console.error("Error adding property to block:", err);
      toast.error("Failed to add property to block");
    }
  };

  // Detach property from block
  const handleDetachProperty = async (propertyId) => {
    try {
      const response = await api.get(`/properties/${propertyId}/block/detach`);
      if (response.data.success || response.data.type === "success") {
        toast.success(response.data.message || "Property detached successfully");
        loadBlockData();
      } else {
        toast.error(response.data.message || "Failed to detach property");
      }
    } catch (err) {
      console.error("Error detaching property:", err);
      toast.error("Failed to detach property from block");
    }
  };

  // Certificate type options - matching ClientShow
  const certificateTypes = [
    'All',
    'Gas Safe Certificate',
    'NIC Certificate',
    'Asbestos Register',
    'Fire Risk Assessment',
    'Legionella',
    'Fire Alarm',
    'Emergency Lighting',
    'Fire Extinguisher Certificate',
    'PAT Testing',
    '240V Certificate'
  ];

  // Reset to page 1 when certificates filters change
  const handleCertificatesSearch = (value) => {
    setCertificatesSearch(value);
    setCertificatesPage(1);
  };

  const handleCertificateTypeFilter = (type) => {
    setCertificateTypeFilter(type);
    setCertificatesPage(1);
  };

  const handleCertificateStatusFilter = (status) => {
    setCertificateStatusFilter(status);
    setCertificatesPage(1);
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this report?")) {
      return;
    }

    try {
      const response = await api.delete(`/clients/${block.client_id}/reports/monthly-inspections/${reportId}`);
      if (response.data.success) {
        toast.success("Report deleted successfully");
        loadBlockData();
      } else {
        toast.error(response.data.message || "Failed to delete report");
      }
    } catch (error) {
      console.error("Error deleting report:", error);
      toast.error("Failed to delete report");
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="container">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!block) {
    return (
      <div className="container">
        <div className="alert alert-warning">Block not found</div>
      </div>
    );
  }

  // Filter and paginate properties
  const filteredProperties = properties.filter(p => {
    if (!propertiesSearchFilter) return true;
    const search = propertiesSearchFilter.toLowerCase();
    return (
      (p.name && p.name.toLowerCase().includes(search)) ||
      (p.property_id && p.property_id.toLowerCase().includes(search)) ||
      (p.postcode && p.postcode.toLowerCase().includes(search))
    );
  });

  const totalPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const paginatedProperties = filteredProperties.slice(
    (propertiesPage - 1) * propertiesPerPage,
    propertiesPage * propertiesPerPage
  );

  // Filter and paginate certificates
  const filteredCertificates = certificates.filter(cert => {
    // Property Type filter
    if (certificatePropertyTypeFilter === 'Active only' && !cert.is_property_active) {
      return false;
    }

    // Type filter
    if (certificateTypeFilter !== 'All' && cert.type !== certificateTypeFilter) {
      return false;
    }

    // Status filter
    if (certificateStatusFilter !== 'All') {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const expiration = cert.expiration ? new Date(cert.expiration) : null;
      if (expiration) expiration.setHours(0, 0, 0, 0);
      const fourWeeksFromNow = new Date(now.getTime() + 28 * 24 * 60 * 60 * 1000); // 4 weeks = 28 days

      if (certificateStatusFilter === 'Active' && (cert.status === 'archived' || !expiration || expiration < now)) return false;
      if (certificateStatusFilter === 'Expired' && (cert.status === 'archived' || !expiration || expiration >= now)) return false;
      if (certificateStatusFilter === 'Due to expire' && (cert.status === 'archived' || !expiration || expiration <= now || expiration > fourWeeksFromNow)) return false;
      if (certificateStatusFilter === 'Archived' && cert.status !== 'archived') return false;
    }

    // Search filter
    if (certificatesSearch) {
      const searchLower = certificatesSearch.toLowerCase();
      return (
        (cert.type && cert.type.toLowerCase().includes(searchLower)) ||
        (cert.property_name && cert.property_name.toLowerCase().includes(searchLower)) ||
        (cert.name && cert.name.toLowerCase().includes(searchLower))
      );
    }

    return true;
  });

  const totalCertificatesPages = Math.ceil(filteredCertificates.length / certificatesPerPage);
  const paginatedCertificates = filteredCertificates.slice(
    (certificatesPage - 1) * certificatesPerPage,
    certificatesPage * certificatesPerPage
  );

  const breadcrumbs = [
    { label: "Dashboard", link: "/dashboard" },
    { label: "Clients", link: "/clients" },
    { label: block.client_name || `Client #${block.client_id}`, link: `/clients/${block.client_id}` },
    { label: "Blocks", link: "" },
    { label: block.name, link: "" },
  ];

  // Permissions
  const canManageBlocks = hasPermission("blocks.manage");
  const canEmailGuardians = hasPermission("properties.emails.guardians");
  const canManageReports = hasPermission("properties.reports.monthly_inspection");

  return (
    <div className="container">
      <Breadcrumbs items={breadcrumbs} />

      {/* Block Overview */}
      <div className="row">
        <div className="col-md-12">
          <div style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #ddd',
              background: '#f8f8f8',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Block</h3>
              <div>
                {canManageBlocks && (
                  <Link
                    to={`/blocks/${id}/edit`}
                    style={{
                      padding: '8px 16px',
                      background: '#337ab7',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      marginRight: '10px',
                      display: 'inline-block',
                      fontSize: '14px'
                    }}
                  >
                    <i className="glyphicon glyphicon-edit" style={{ marginRight: '5px' }}></i> Edit
                  </Link>
                )}
                {canEmailGuardians && (
                  <Link
                    to={`/blocks/${id}/emails/guardians/create`}
                    style={{
                      padding: '8px 16px',
                      background: '#5bc0de',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      display: 'inline-block',
                      fontSize: '14px'
                    }}
                  >
                    <i className="fa fa-envelope" style={{ marginRight: '5px' }}></i> Email guardians
                  </Link>
                )}
              </div>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#666', display: 'inline-block', width: '180px' }}>Name:</span>
                <span>{block.name}</span>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#666', display: 'inline-block', width: '180px' }}>Client:</span>
                <Link to={`/clients/${block.client_id}`} style={{ color: '#337ab7', textDecoration: 'underline' }}>
                  {block.client_name || `Client #${block.client_id}`}
                </Link>
              </div>
              <div>
                <span style={{ fontWeight: '600', color: '#666', display: 'inline-block', width: '180px' }}>Number of properties:</span>
                <span>{block.properties_count || properties.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className="row" style={{ marginTop: '20px' }}>
        <div className="col-xs-12">
          <div style={{
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #ddd',
              background: '#f8f8f8'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Properties</h3>
            </div>
            <div style={{ padding: '20px' }}>
              {/* Add Property Search */}
              {canManageBlocks && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#30C693',
                    fontSize: '13px'
                  }}>
                    Find property which you want to add to the block. It will be added after click on suggestion in dropdown.
                  </label>
                  <div style={{ position: 'relative', display: 'inline-block', width: '100%', maxWidth: '400px' }}>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Type property ID or name (or *)"
                      value={propertySearch}
                      onChange={(e) => handlePropertySearch(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #30C693',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                    {searchResults.length > 0 && (
                      <ul style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        border: '1px solid #ddd',
                        borderTop: 'none',
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {searchResults.map(p => (
                          <li
                            key={p.id}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              fontSize: '14px'
                            }}
                            onClick={() => handleAddProperty(p.id)}
                            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.background = '#fff'}
                          >
                            {p.name} ({p.property_id})
                          </li>
                        ))}
                      </ul>
                    )}
                    {searching && (
                      <span style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        fontSize: '12px'
                      }}>Searching...</span>
                    )}
                  </div>
                </div>
              )}

              {/* Table Controls - DataTables style */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px',
                flexWrap: 'wrap',
                gap: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                  <span>Show</span>
                  <select
                    value={propertiesPerPage}
                    onChange={(e) => {
                      setPropertiesPerPage(Number(e.target.value));
                      setPropertiesPage(1);
                    }}
                    style={{
                      padding: '6px 10px',
                      margin: '0 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>entries</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                  <span style={{ marginRight: '8px' }}>Search:</span>
                  <input
                    type="text"
                    value={propertiesSearchFilter}
                    onChange={(e) => {
                      setPropertiesSearchFilter(e.target.value);
                      setPropertiesPage(1);
                    }}
                    style={{
                      padding: '6px 10px',
                      width: '200px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>

              {/* Properties Table - DataTables style */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ background: '#f9f9f9' }}>
                      <th style={{
                        padding: '12px 15px',
                        borderBottom: '2px solid #ddd',
                        textAlign: 'left',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>Property Name</th>
                      <th style={{
                        padding: '12px 15px',
                        borderBottom: '2px solid #ddd',
                        textAlign: 'left',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>Property ID</th>
                      <th style={{
                        padding: '12px 15px',
                        borderBottom: '2px solid #ddd',
                        textAlign: 'left',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>Postcode</th>
                      <th style={{
                        padding: '12px 15px',
                        borderBottom: '2px solid #ddd',
                        textAlign: 'left',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>Status</th>
                      <th style={{
                        padding: '12px 15px',
                        borderBottom: '2px solid #ddd',
                        textAlign: 'center',
                        fontWeight: '600',
                        width: '100px'
                      }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProperties.length > 0 ? (
                      paginatedProperties.map((property, index) => (
                        <tr key={property.id} style={{
                          background: index % 2 === 0 ? '#fff' : '#f9f9f9',
                          borderBottom: '1px solid #ddd'
                        }}>
                          <td style={{ padding: '12px 15px' }}>
                            <Link to={`/properties/${property.id}`} style={{ color: '#337ab7', textDecoration: 'none' }}>
                              {property.name}
                            </Link>
                          </td>
                          <td style={{ padding: '12px 15px' }}>{property.property_id}</td>
                          <td style={{ padding: '12px 15px' }}>{property.postcode}</td>
                          <td style={{ padding: '12px 15px' }}>{property.status}</td>
                          <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                            {canManageBlocks && (
                              <button
                                onClick={() => handleDetachProperty(property.id)}
                                style={{
                                  padding: '5px 12px',
                                  background: '#d9534f',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '3px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                Detach
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                          No properties in this block
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination - DataTables style */}
              {filteredProperties.length > 0 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '15px',
                  flexWrap: 'wrap',
                  gap: '10px',
                  fontSize: '14px'
                }}>
                  <div style={{ color: '#666' }}>
                    Showing {((propertiesPage - 1) * propertiesPerPage) + 1} to {Math.min(propertiesPage * propertiesPerPage, filteredProperties.length)} of {filteredProperties.length} entries
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <button
                      disabled={propertiesPage === 1}
                      onClick={() => setPropertiesPage(p => p - 1)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        background: propertiesPage === 1 ? '#f5f5f5' : '#fff',
                        color: propertiesPage === 1 ? '#999' : '#333',
                        cursor: propertiesPage === 1 ? 'not-allowed' : 'pointer',
                        borderRadius: '3px 0 0 3px',
                        fontSize: '13px'
                      }}
                    >
                      Previous
                    </button>
                    {[...Array(Math.min(5, totalPages || 1))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPropertiesPage(pageNum)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #ddd',
                            borderLeft: 'none',
                            background: propertiesPage === pageNum ? '#337ab7' : '#fff',
                            color: propertiesPage === pageNum ? '#fff' : '#333',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      disabled={propertiesPage === totalPages || totalPages === 0}
                      onClick={() => setPropertiesPage(p => p + 1)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        borderLeft: 'none',
                        background: (propertiesPage === totalPages || totalPages === 0) ? '#f5f5f5' : '#fff',
                        color: (propertiesPage === totalPages || totalPages === 0) ? '#999' : '#333',
                        cursor: (propertiesPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                        borderRadius: '0 3px 3px 0',
                        fontSize: '13px'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Certificates Section */}
      <div className="row" style={{ marginTop: '20px' }}>
        <div className="col-xs-12">
          <div style={{
            background: '#f8f9fa',
            borderRadius: '8px',
            boxShadow: '0px 2px 0px 0px #e9ecef',
            marginBottom: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1.2rem 1.5rem 1rem 16px'
            }}>
              <div style={{ fontSize: '29px', fontWeight: '600', fontFamily: '"Stag", serif' }}>Certificates</div>
            </div>

            {/* Certificate Filters */}
            <div style={{ padding: '15px 20px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontWeight: '600', color: '#333', minWidth: '80px', marginRight: '10px' }}>Certificate</span>
                {certificateTypes.map((type) => (
                  <label key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px', color: '#333', whiteSpace: 'nowrap' }}>
                    <input
                      type="radio"
                      name="certificateType"
                      checked={certificateTypeFilter === type}
                      onChange={() => handleCertificateTypeFilter(type)}
                      style={{ margin: 0, cursor: 'pointer', accentColor: '#e74c3c' }}
                    />
                    {type}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ fontWeight: '600', color: '#333', minWidth: '80px', marginRight: '10px' }}>Status</span>
                {['All', 'Active', 'Archived', 'Due to expire', 'Expired'].map((status) => (
                  <label key={status} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px', color: '#333', whiteSpace: 'nowrap' }}>
                    <input
                      type="radio"
                      name="certificateStatus"
                      checked={certificateStatusFilter === status}
                      onChange={() => handleCertificateStatusFilter(status)}
                      style={{ margin: 0, cursor: 'pointer', accentColor: '#e74c3c' }}
                    />
                    {status}
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '600', color: '#333', minWidth: '80px', marginRight: '10px' }}>Property Type</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px', color: '#333', whiteSpace: 'nowrap' }}>
                  <input
                    type="radio"
                    name="propertyType"
                    checked={certificatePropertyTypeFilter === 'All'}
                    onChange={() => { setCertificatePropertyTypeFilter('All'); setCertificatesPage(1); }}
                    style={{ margin: 0, cursor: 'pointer', accentColor: '#e74c3c' }}
                  />
                  All
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '13px', color: '#333', whiteSpace: 'nowrap' }}>
                  <input
                    type="radio"
                    name="propertyType"
                    checked={certificatePropertyTypeFilter === 'Active only'}
                    onChange={() => { setCertificatePropertyTypeFilter('Active only'); setCertificatesPage(1); }}
                    style={{ margin: 0, cursor: 'pointer', accentColor: '#e74c3c' }}
                  />
                  Active only
                </label>
              </div>
            </div>

            {certificates.length > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', flexWrap: 'wrap', gap: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                    <span>Show</span>
                    <select
                      value={certificatesPerPage}
                      onChange={(e) => {
                        setCertificatesPerPage(Number(e.target.value));
                        setCertificatesPage(1);
                      }}
                      style={{ padding: '6px 10px', margin: '0 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
                    <input
                      type="text"
                      value={certificatesSearch}
                      onChange={(e) => handleCertificatesSearch(e.target.value)}
                      placeholder="Search certificates..."
                      style={{ padding: '6px 10px', width: '200px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                    />
                  </div>
                </div>

                <div style={{ overflowX: 'auto', padding: '0 20px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                    <thead>
                      <tr style={{ background: '#f9f9f9' }}>
                        <th style={{ padding: '12px 15px', borderBottom: '2px solid #ddd', textAlign: 'left', fontWeight: '600' }}>Type</th>
                        <th style={{ padding: '12px 15px', borderBottom: '2px solid #ddd', textAlign: 'left', fontWeight: '600' }}>Completed</th>
                        <th style={{ padding: '12px 15px', borderBottom: '2px solid #ddd', textAlign: 'left', fontWeight: '600' }}>Expiration</th>
                        <th style={{ padding: '12px 15px', borderBottom: '2px solid #ddd', textAlign: 'left', fontWeight: '600' }}>Contractor Appointment</th>
                        <th style={{ padding: '12px 15px', borderBottom: '2px solid #ddd', textAlign: 'left', fontWeight: '600' }}>Property</th>
                        <th style={{ padding: '12px 15px', borderBottom: '2px solid #ddd', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCertificates.map((cert, index) => (
                        <tr key={cert.id} style={{ background: index % 2 === 0 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #ddd' }}>
                          <td style={{ padding: '12px 15px' }}>{cert.type || 'N/A'}</td>
                          <td style={{ padding: '12px 15px' }}>{cert.completed ? new Date(cert.completed).toLocaleDateString('en-GB') : '-'}</td>
                          <td style={{ padding: '12px 15px' }}>{cert.expiration ? new Date(cert.expiration).toLocaleDateString('en-GB') : '-'}</td>
                          <td style={{ padding: '12px 15px' }}>{cert.contractor_appointment ? new Date(cert.contractor_appointment).toLocaleDateString('en-GB') : '-'}</td>
                          <td style={{ padding: '12px 15px' }}>
                            {cert.property_id ? (
                              <Link to={`/properties/${cert.property_id}`} style={{ color: '#337ab7', textDecoration: 'none' }}>
                                {cert.property_name || 'N/A'}
                              </Link>
                            ) : (
                              cert.property_name || 'N/A'
                            )}
                          </td>
                          <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                            <button
                              onClick={async () => {
                                try {
                                  const response = await api.get(`/certificates/${cert.id}/download`);
                                  if (response.data.success && response.data.downloadUrl) {
                                    window.open(response.data.downloadUrl, '_blank');
                                  } else {
                                    toast.error('Unable to download certificate: ' + (response.data.message || 'Unknown error'));
                                  }
                                } catch (error) {
                                  console.error('Download error:', error);
                                  toast.error('Error downloading certificate');
                                }
                              }}
                              style={{ padding: '5px 10px', background: '#5bc0de', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontSize: '12px', marginRight: '5px' }}
                            >
                              Download
                            </button>
                            <Link
                              to={`/properties/${cert.property_id}/certificate/${cert.id}/edit`}
                              style={{ padding: '5px 10px', background: '#337ab7', color: '#fff', border: 'none', borderRadius: '3px', textDecoration: 'none', fontSize: '12px', marginRight: '5px', display: 'inline-block' }}
                            >
                              Edit
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', flexWrap: 'wrap', gap: '10px', fontSize: '14px' }}>
                  <div style={{ color: '#666' }}>
                    Showing {filteredCertificates.length === 0 ? 0 : ((certificatesPage - 1) * certificatesPerPage) + 1} to {Math.min(certificatesPage * certificatesPerPage, filteredCertificates.length)} of {filteredCertificates.length} entries
                    {certificatesSearch && ` (filtered from ${certificates.length} total entries)`}
                  </div>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <button
                      disabled={certificatesPage === 1}
                      onClick={() => setCertificatesPage(p => p - 1)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        background: certificatesPage === 1 ? '#f5f5f5' : '#fff',
                        color: certificatesPage === 1 ? '#999' : '#333',
                        cursor: certificatesPage === 1 ? 'not-allowed' : 'pointer',
                        borderRadius: '3px 0 0 3px',
                        fontSize: '13px'
                      }}
                    >
                      Previous
                    </button>
                    {[...Array(Math.min(5, totalCertificatesPages || 1))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCertificatesPage(pageNum)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #ddd',
                            borderLeft: 'none',
                            background: certificatesPage === pageNum ? '#337ab7' : '#fff',
                            color: certificatesPage === pageNum ? '#fff' : '#333',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      disabled={certificatesPage === totalCertificatesPages || totalCertificatesPages === 0}
                      onClick={() => setCertificatesPage(p => p + 1)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #ddd',
                        borderLeft: 'none',
                        background: (certificatesPage === totalCertificatesPages || totalCertificatesPages === 0) ? '#f5f5f5' : '#fff',
                        color: (certificatesPage === totalCertificatesPages || totalCertificatesPages === 0) ? '#999' : '#333',
                        cursor: (certificatesPage === totalCertificatesPages || totalCertificatesPages === 0) ? 'not-allowed' : 'pointer',
                        borderRadius: '0 3px 3px 0',
                        fontSize: '13px'
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>No certificate!</div>
            )}
          </div>
        </div>
      </div>

      {/* Reports Section */}
      {reports.length > 0 && (
        <div className="row" style={{ marginTop: '20px' }}>
          <div className="col-md-12">
            <div style={{
              background: '#fff',
              border: '1px solid #ddd',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <div style={{
                padding: '15px 20px',
                borderBottom: '1px solid #ddd',
                background: '#f8f8f8'
              }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Reports</h3>
              </div>
              <div style={{ padding: '20px' }}>
                <div className="row">
                  {reports.map((report) => (
                    <div key={report.id} className="col-xs-12 col-sm-6 col-md-3" style={{ marginBottom: '15px' }}>
                      <div
                        onClick={async () => {
                          try {
                            // Download PDF using fetch with proper binary handling
                            const token = localStorage.getItem('token');
                            const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
                            const response = await fetch(`${baseUrl}/api/monthlyinspectionreports/${report.id}/download-pdf`, {
                              method: 'GET',
                              headers: {
                                'Authorization': `Bearer ${token}`,
                              },
                            });

                            if (!response.ok) {
                              throw new Error(`HTTP error! status: ${response.status}`);
                            }

                            // Get filename from content-disposition header or use default
                            const contentDisposition = response.headers.get('content-disposition');
                            let filename = `Monthly_Report_${report.months}_${report.year}.pdf`;
                            if (contentDisposition) {
                              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                              if (filenameMatch && filenameMatch[1]) {
                                filename = filenameMatch[1].replace(/['"]/g, '');
                              }
                            }

                            // Create blob and trigger download
                            const blob = await response.blob();
                            const blobUrl = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(blobUrl);
                            document.body.removeChild(a);
                            toast.success('Report downloaded successfully');
                          } catch (error) {
                            console.error('Download error:', error);
                            toast.error('Error downloading report');
                          }
                        }}
                        style={{
                          display: 'flex',
                          background: '#34495e',
                          borderRadius: '4px',
                          padding: '12px',
                          cursor: 'pointer',
                          color: '#fff',
                          minHeight: '70px',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ marginRight: '12px' }}>
                          <img src="/assets/images/pdf.png" alt="PDF" style={{ width: '40px', height: 'auto' }} />
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: '600', fontSize: '14px', color: '#fff' }}>
                            {report.months} ({report.year})
                          </p>
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#bdc3c7' }}>
                            Updated: {new Date(report.updated_at).toLocaleDateString("en-GB")}
                          </p>
                        </div>
                      </div>
                      {canManageReports && (
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '8px',
                            marginTop: '0',
                            background: '#e74c3c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '0 0 4px 4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '500'
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockShow;
